import React from "react";
import { formatCurrency, formatPercentage } from "../../../../utils/formatters";
import { motion, AnimatePresence } from "framer-motion";

const FinancialAssumptionsForm = ({ data, onChange, isMarried }) => {
  const incomeGoalTypes = {
    1: "Retirement Income",
    2: "Travel",
    3: "Healthcare",
    4: "Education",
    5: "Home Purchase",
    6: "Home Improvement",
    7: "Vehicle Purchase",
    8: "Business Investment",
    9: "Other",
  };

  const handleChange = (field, value) => {
    const updatedData = { ...data };
    if (field.includes(".")) {
      const [category, subfield] = field.split(".");
      updatedData[category][subfield] = parseFloat(value) || 0;
    } else {
      updatedData[field] = parseFloat(value) || 0;
    }
    onChange(updatedData);
  };

  const handleAddIncomeGoal = () => {
    const updatedData = { ...data };
    if (!updatedData.additionalIncomeGoals) {
      updatedData.additionalIncomeGoals = [];
    }
    updatedData.additionalIncomeGoals.push({
      type: "",
      amount: "",
      startDate: "",
      endDate: "",
      otherType: "",
    });
    onChange(updatedData);
  };

  const handleIncomeGoalChange = (index, field, value) => {
    const updatedData = { ...data };
    updatedData.additionalIncomeGoals[index][field] = value;
    onChange(updatedData);
  };

  const handleDeleteIncomeGoal = (index) => {
    const updatedData = { ...data };
    updatedData.additionalIncomeGoals =
      updatedData.additionalIncomeGoals.filter((_, i) => i !== index);
    onChange(updatedData);
  };

  return (
    <div className="border rounded-lg p-6">
      <h2 className="text-xl font-semibold mb-4">
        Financial Goals & Assumptions
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Base Monthly Income Goal */}
        <div>
          <label className="block mb-2">Base Monthly Net Income Goal</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={data.monthlyNetIncomeGoal || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/[^\d.]/g, "");
              handleChange("monthlyNetIncomeGoal", value);
            }}
            onBlur={(e) => {
              const value = parseFloat(e.target.value);
              if (!isNaN(value)) {
                handleChange("monthlyNetIncomeGoal", value);
              }
            }}
            placeholder="0"
          />
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {formatCurrency(data.monthlyNetIncomeGoal || 0)}
          </div>
        </div>

        {/* Additional Income Goals */}
        <div className="col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Additional Income Goals</h3>
            <button
              onClick={handleAddIncomeGoal}
              type="button"
              className="inline-flex items-center px-4 py-2 rounded-full border border-transparent shadow-sm text-sm font-medium text-white bg-[#131d80] hover:bg-[#0f1660]"
            >
              <svg
                className="w-5 h-5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Income Goal
            </button>
          </div>

          <AnimatePresence>
            {data.additionalIncomeGoals?.map((goal, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-surface-default dark:bg-surface-default rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <select
                      value={goal.type}
                      onChange={(e) =>
                        handleIncomeGoalChange(index, "type", e.target.value)
                      }
                      className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Type</option>
                      {Object.entries(incomeGoalTypes).map(([id, name]) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </select>
                    <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                      Goal Type
                    </label>
                  </div>

                  {goal.type === "9" && (
                    <div className="relative">
                      <input
                        type="text"
                        value={goal.otherType}
                        onChange={(e) =>
                          handleIncomeGoalChange(
                            index,
                            "otherType",
                            e.target.value,
                          )
                        }
                        className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder=" "
                      />
                      <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                        Specify Other Goal Type
                      </label>
                    </div>
                  )}

                  <div className="relative">
                    <input
                      type="text"
                      value={goal.amount}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, "");
                        handleIncomeGoalChange(index, "amount", value);
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value)) {
                          handleIncomeGoalChange(
                            index,
                            "amount",
                            formatCurrency(value, false).replace(/[$,]/g, ""),
                          );
                        }
                      }}
                      className="peer w-full h-12 border border-gray-300 rounded-lg px-8 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                      placeholder=" "
                    />
                    <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                      Monthly Amount
                    </label>
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-500">
                      $
                    </span>
                  </div>

                  <div className="relative">
                    <input
                      type="date"
                      value={goal.startDate}
                      onChange={(e) =>
                        handleIncomeGoalChange(
                          index,
                          "startDate",
                          e.target.value,
                        )
                      }
                      className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                      Start Date
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="date"
                      value={goal.endDate}
                      onChange={(e) =>
                        handleIncomeGoalChange(index, "endDate", e.target.value)
                      }
                      className="peer w-full h-12 border border-gray-300 rounded-lg px-3 pt-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <label className="absolute top-0 left-3 text-gray-500 dark:text-gray-500 text-xs transition-all duration-200 peer-focus:text-blue-500 mt-1">
                      End Date
                    </label>
                  </div>

                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => handleDeleteIncomeGoal(index)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200 rounded-full hover:bg-red-50"
                      title="Remove goal"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Market Assumptions */}
        <div>
          <label className="block mb-2">Expected Inflation Rate (%)</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={data.inflationRate || ""}
            onChange={(e) => handleChange("inflationRate", e.target.value)}
            step="0.1"
            placeholder="0.0"
          />
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {formatPercentage(data.inflationRate || 0)}
          </div>
        </div>

        <div>
          <label className="block mb-2">Expected Investment Return (%)</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={data.investmentReturn || ""}
            onChange={(e) => handleChange("investmentReturn", e.target.value)}
            step="0.1"
            placeholder="0.0"
          />
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {formatPercentage(data.investmentReturn || 0)}
          </div>
        </div>

        {/* Life Expectancy */}
        <div>
          <label className="block mb-2">Life Expectancy (Primary)</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={data.lifeExpectancy.firstMember || ""}
            onChange={(e) =>
              handleChange("lifeExpectancy.firstMember", e.target.value)
            }
            min="0"
            max="120"
          />
        </div>

        {isMarried && (
          <div>
            <label className="block mb-2">Life Expectancy (Spouse)</label>
            <input
              type="number"
              className="w-full p-2 border rounded"
              value={data.lifeExpectancy.secondMember || ""}
              onChange={(e) =>
                handleChange("lifeExpectancy.secondMember", e.target.value)
              }
              min="0"
              max="120"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialAssumptionsForm;
